<!-- Renders a single inspector control for one element attribute, mapping ControlKind -> Pulse component. -->
<template>
  <div class="flex flex-col gap-1">
    <Toggle
      v-if="control.kind === 'toggle'"
      :model-value="boolValue"
      :label="control.label"
      @update:model-value="onToggle"
    />

    <!-- Length: a number and a unit, so nobody has to know the value is really a CSS string. -->
    <div v-else-if="control.kind === 'length' && !isFreeform" class="flex flex-col gap-1">
      <label class="text-xsm text-typography-secondary">{{ control.label }}</label>
      <div class="flex items-center gap-2">
        <Input
          type="number"
          class="flex-1"
          :disabled="valueIsKeyword"
          :model-value="valueIsKeyword ? '' : lengthAmount"
          @update:model-value="v => onLength(v, lengthUnit)"
        />
        <Dropdown
          class="w-24"
          :model-value="lengthUnit"
          :items="unitItems"
          @update:model-value="u => onLength(lengthAmount, String(u))"
        />
      </div>
    </div>

    <Dropdown
      v-else-if="isSelect"
      :model-value="stringValue"
      :items="selectItems"
      :label="control.label"
      :placeholder="control.placeholder || 'Select…'"
      @update:model-value="v => emit('update:modelValue', v)"
    />
    <ColorInput
      v-else-if="control.kind === 'color'"
      :model-value="stringValue"
      :label="control.label"
      @update:model-value="v => emit('update:modelValue', v)"
    />
    <TextArea
      v-else-if="control.kind === 'textarea'"
      :model-value="stringValue"
      :label="control.label"
      @update:model-value="v => emit('update:modelValue', v)"
    />
    <Input
      v-else-if="control.kind === 'number'"
      type="number"
      :model-value="stringValue"
      :label="control.label"
      @update:model-value="v => emit('update:modelValue', toNumber(v))"
    />
    <Input
      v-else
      type="text"
      :model-value="stringValue"
      :label="control.label"
      :placeholder="placeholder"
      @update:model-value="v => emit('update:modelValue', v)"
    />

    <p v-if="error" class="text-2xs text-danger-700">{{ error }}</p>
    <p v-if="control.help" class="text-2xs text-typography-disabled">{{ control.help }}</p>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { Input, ColorInput, Dropdown, Toggle, TextArea } from '@pulse/components';
import type { ControlSchema } from '~/builder/catalog';
import { FONT_OPTIONS } from '~/builder/catalog';

const DEFAULT_UNITS = ['px', '%', 'em', 'rem'];

/**
 * Stands for "no unit" in the picker. CSS accepts some lengths bare — `line-height: 1.5` is a
 * multiplier — so appending a unit to one changes its meaning rather than restating it.
 */
const UNITLESS = '—';

/** A single number with an optional unit — the only shape the number+unit pair can round-trip. */
const SINGLE_LENGTH = /^\s*(-?\d*\.?\d+)\s*(px|%|em|rem|vw|vh)?\s*$/;

const props = defineProps<{
  control: ControlSchema;
  modelValue: string | number | boolean | undefined;
  error?: string;
}>();

const emit = defineEmits<{
  'update:modelValue': [value: string | number | boolean];
}>();

const isSelect = computed(() => props.control.kind === 'select' || props.control.kind === 'font');

const selectItems = computed(() => (props.control.kind === 'font' ? FONT_OPTIONS : props.control.options || []));

const stringValue = computed(() => (props.modelValue == null ? '' : String(props.modelValue)));

const boolValue = computed(() => {
  if (props.control.onValue !== undefined) return stringValue.value === props.control.onValue;

  return props.modelValue === true || props.modelValue === 'true';
});

const parsedLength = computed(() => SINGLE_LENGTH.exec(stringValue.value));

/**
 * `auto`, `1 1 0`, `8px 16px` and the like cannot be expressed as one number plus one unit.
 * Rather than silently flatten a merchant's value, fall back to the plain text field.
 */
const keywords = computed(() => props.control.keywords ?? []);

/** True when the value in hand is one of the keywords this attribute accepts. */
const valueIsKeyword = computed(() => keywords.value.includes(stringValue.value.trim()));

const isFreeform = computed(() => stringValue.value.trim() !== '' && !parsedLength.value && !valueIsKeyword.value);

const lengthAmount = computed(() => parsedLength.value?.[1] ?? '');

const units = computed(() => props.control.units ?? DEFAULT_UNITS);

/** True when the value in hand is a bare number, so the picker must be able to say so. */
const valueIsUnitless = computed(() => !!parsedLength.value && !parsedLength.value[2]);

const lengthUnit = computed(() => {
  if (valueIsKeyword.value) return stringValue.value.trim();
  // Nothing set yet: the first real unit is the sensible starting point.
  if (!parsedLength.value) return units.value[0];

  return parsedLength.value[2] || UNITLESS;
});

// Offered where the attribute genuinely takes a bare number, and always where the current value
// already is one — otherwise editing it would quietly append a unit and change what it means.
const unitItems = computed(() => {
  const items = units.value.map(u => ({ label: u, value: u }));
  if (props.control.unitless || valueIsUnitless.value) items.push({ label: UNITLESS, value: UNITLESS });
  // A keyword replaces the measurement entirely, so it belongs beside the units, not in a text box.
  for (const k of keywords.value) items.push({ label: k === 'normal' ? 'Default' : k, value: k });

  return items;
});

const placeholder = computed(() => props.control.placeholder);

function onToggle(next: unknown) {
  const on = next === true || next === 'true';
  if (props.control.onValue === undefined) return emit('update:modelValue', on);

  emit('update:modelValue', on ? props.control.onValue : (props.control.offValue ?? ''));
}

function onLength(amount: string | number | unknown, unit: string) {
  if (keywords.value.includes(unit)) return emit('update:modelValue', unit);

  const raw = String(amount ?? '').trim();
  // Clearing the number clears the attribute rather than writing a bare unit.
  if (raw === '') return emit('update:modelValue', '');

  emit('update:modelValue', unit === UNITLESS ? raw : `${raw}${unit}`);
}

function toNumber(value: unknown): number {
  const n = Number(value);

  return Number.isFinite(n) ? n : 0;
}
</script>
